import { Habit } from "../types";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const ALL_TRACKED_DAYS = [0, 1, 2, 3, 4, 5, 6];

export const formatDateToLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

const getDateDiffInDays = (from: Date, to: Date) => {
    const fromMidnight = new Date(from);
    const toMidnight = new Date(to);
    fromMidnight.setHours(0, 0, 0, 0);
    toMidnight.setHours(0, 0, 0, 0);
    return Math.round((toMidnight.getTime() - fromMidnight.getTime()) / MS_PER_DAY);
};

export const getMondayBasedDayIndex = (date: Date) => {
    return (date.getDay() + 6) % 7;
};

export const getHabitTrackedDays = (habit: Habit) => {
    const trackedDays = habit.trackedDays;

    if (!trackedDays || trackedDays.length === 0) {
        return ALL_TRACKED_DAYS;
    }

    return Array.from(
        new Set(trackedDays.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)),
    ).sort((a, b) => a - b);
};

export const getHabitMonthlyDay = (habit: Habit) => {
    const day = habit.monthlyDay;
    return Number.isInteger(day) && day! >= 1 && day! <= 31 ? day! : 1;
};

// `periodTargetDays` marks the new period-goal behaviour. Older saved schedules
// keep their weekday/day-of-month behaviour until the user edits them.
export const isPeriodHabit = (habit: Habit) => (
    (habit.frequency === 'weekly' || habit.frequency === 'monthly') && !!habit.periodTargetDays
);

export const getHabitPeriodTargetDays = (habit: Habit, dateStr?: string) => {
    const date = dateStr ? parseLocalDate(dateStr) : new Date();
    const maximum = habit.frequency === 'weekly'
        ? 7
        : habit.frequency === 'monthly'
            ? new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
            : 31;
    const target = habit.periodTargetDays || 1;
    return Math.min(Math.max(Math.floor(target), 1), maximum);
};

const getPeriodStart = (habit: Habit, date: Date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    if (habit.frequency === 'weekly') {
        start.setDate(start.getDate() - getMondayBasedDayIndex(start));
    } else if (habit.frequency === 'monthly') {
        start.setDate(1);
    }
    return start;
};

const getPeriodEnd = (habit: Habit, date: Date) => {
    const start = getPeriodStart(habit, date);
    const end = new Date(start);
    if (habit.frequency === 'weekly') end.setDate(end.getDate() + 6);
    if (habit.frequency === 'monthly') end.setMonth(end.getMonth() + 1, 0);
    return end;
};

export const getHabitPeriodProgress = (habit: Habit, dateStr: string) => {
    if (!isPeriodHabit(habit)) return getHabitProgressForDate(habit, dateStr);

    const date = parseLocalDate(dateStr);
    const start = formatDateToLocal(getPeriodStart(habit, date));
    const end = formatDateToLocal(getPeriodEnd(habit, date));
    return getHabitCompletedDates(habit).filter((completedDate) => completedDate >= start && completedDate <= end).length;
};

export const isHabitPeriodCompleted = (habit: Habit, dateStr: string) => (
    isPeriodHabit(habit) && getHabitPeriodProgress(habit, dateStr) >= getHabitPeriodTargetDays(habit, dateStr)
);

export const isHabitDateTracked = (habit: Habit, dateStr: string) => {
    const date = parseLocalDate(dateStr);
    if (isPeriodHabit(habit)) return true;
    if (habit.frequency === 'monthly') return date.getDate() === getHabitMonthlyDay(habit);
    return getHabitTrackedDays(habit).includes(getMondayBasedDayIndex(date));
};

export const isHabitDateCompleted = (habit: Habit, dateStr: string) => {
    return isHabitDateTracked(habit, dateStr) && getHabitProgressForDate(habit, dateStr) >= getHabitTargetCount(habit);
};

export const getHabitTargetCount = (habit: Habit) => {
    return habit.count && habit.count > 0 ? habit.count : 1;
};

export const getHabitProgressForDate = (habit: Habit, dateStr: string) => {
    if (!isHabitDateTracked(habit, dateStr)) {
        return 0;
    }

    const targetCount = getHabitTargetCount(habit);
    const storedProgress = habit.progressByDate?.[dateStr];

    if (typeof storedProgress === 'number') {
        return Math.min(Math.max(storedProgress, 0), targetCount);
    }

    return (habit.completedDates || []).includes(dateStr) ? targetCount : 0;
};

export const getHabitProgressRatioForDate = (habit: Habit, dateStr: string) => {
    return getHabitProgressForDate(habit, dateStr) / getHabitTargetCount(habit);
};

export const advanceHabitProgressDate = (habit: Habit, dateStr: string) => {
    if (!isHabitDateTracked(habit, dateStr)) {
        return {
            completedDates: habit.completedDates || [],
            progressByDate: habit.progressByDate,
        };
    }

    const currentProgress = getHabitProgressForDate(habit, dateStr);
    const targetCount = getHabitTargetCount(habit);
    const nextProgress = currentProgress >= targetCount ? 0 : currentProgress + 1;
    const completedDates = habit.completedDates || [];
    const progressByDate = { ...(habit.progressByDate || {}) };

    if (nextProgress > 0) {
        progressByDate[dateStr] = nextProgress;
    } else {
        delete progressByDate[dateStr];
    }

    const updatedCompletedDates = nextProgress >= targetCount
        ? Array.from(new Set([...completedDates, dateStr])).sort()
        : completedDates.filter((completedDate) => completedDate !== dateStr);

    return {
        completedDates: updatedCompletedDates,
        progressByDate: Object.keys(progressByDate).length > 0 ? progressByDate : undefined,
    };
};

export const setHabitProgressDate = (habit: Habit, dateStr: string, progress: number) => {
    if (!isHabitDateTracked(habit, dateStr)) {
        return {
            completedDates: habit.completedDates || [],
            progressByDate: habit.progressByDate,
        };
    }

    const targetCount = getHabitTargetCount(habit);
    const nextProgress = Math.min(Math.max(Math.floor(progress), 0), targetCount);
    const completedDates = habit.completedDates || [];
    const progressByDate = { ...(habit.progressByDate || {}) };

    if (nextProgress > 0) {
        progressByDate[dateStr] = nextProgress;
    } else {
        delete progressByDate[dateStr];
    }

    const updatedCompletedDates = nextProgress >= targetCount
        ? Array.from(new Set([...completedDates, dateStr])).sort()
        : completedDates.filter((completedDate) => completedDate !== dateStr);

    return {
        completedDates: updatedCompletedDates,
        progressByDate: Object.keys(progressByDate).length > 0 ? progressByDate : undefined,
    };
};

export const getHabitTotalCompletions = (habit: Habit) => {
    if (isPeriodHabit(habit)) {
        const periodKeys = new Set(getHabitCompletedDates(habit).map((dateStr) => formatDateToLocal(getPeriodStart(habit, parseLocalDate(dateStr)))));
        return Array.from(periodKeys).filter((periodStart) => isHabitPeriodCompleted(habit, periodStart)).length;
    }
    return getHabitCompletedDates(habit).length;
};

const getHabitCompletedDates = (habit: Habit) => {
    const candidateDates = new Set([
        ...(habit.completedDates || []),
        ...Object.keys(habit.progressByDate || {}),
    ]);

    return Array.from(candidateDates)
        .filter((dateStr) => isHabitDateCompleted(habit, dateStr))
        .sort();
};

const getPreviousTrackedDate = (habit: Habit, date: Date) => {
    const cursor = new Date(date);
    cursor.setDate(cursor.getDate() - 1);

    for (let i = 0; i < 400; i++) {
        if (isHabitDateTracked(habit, formatDateToLocal(cursor))) {
            return cursor;
        }

        cursor.setDate(cursor.getDate() - 1);
    }

    return cursor;
};

const getNextTrackedDate = (habit: Habit, date: Date) => {
    const cursor = new Date(date);
    cursor.setDate(cursor.getDate() + 1);

    for (let i = 0; i < 400; i++) {
        if (isHabitDateTracked(habit, formatDateToLocal(cursor))) {
            return cursor;
        }

        cursor.setDate(cursor.getDate() + 1);
    }

    return cursor;
};

export const getHabitCurrentStreak = (habit: Habit, referenceDate = new Date()) => {
    if (isPeriodHabit(habit)) {
        let cursor = getPeriodStart(habit, referenceDate);
        if (!isHabitPeriodCompleted(habit, formatDateToLocal(cursor))) {
            cursor = getPreviousPeriodStart(habit, cursor);
        }

        let streak = 0;
        while (isHabitPeriodCompleted(habit, formatDateToLocal(cursor))) {
            streak++;
            cursor = getPreviousPeriodStart(habit, cursor);
        }
        return streak;
    }

    const completedDates = getHabitCompletedDates(habit);
    if (completedDates.length === 0) return 0;

    const completedSet = new Set(completedDates);
    let cursor = new Date(referenceDate);

    if (!isHabitDateTracked(habit, formatDateToLocal(cursor)) || !completedSet.has(formatDateToLocal(cursor))) {
        cursor = getPreviousTrackedDate(habit, cursor);
    }

    let streak = 0;
    while (completedSet.has(formatDateToLocal(cursor))) {
        streak++;
        cursor = getPreviousTrackedDate(habit, cursor);
    }

    return streak;
};

export const getHabitBestStreak = (habit: Habit) => {
    if (isPeriodHabit(habit)) {
        const completedPeriods = Array.from(new Set(
            getHabitCompletedDates(habit)
                .map((dateStr) => getPeriodStart(habit, parseLocalDate(dateStr)))
                .filter((periodStart) => isHabitPeriodCompleted(habit, formatDateToLocal(periodStart)))
                .map(formatDateToLocal),
        )).sort();
        if (completedPeriods.length === 0) return 0;

        let best = 1;
        let current = 1;
        for (let i = 1; i < completedPeriods.length; i++) {
            const expected = getNextPeriodStart(habit, parseLocalDate(completedPeriods[i - 1]));
            if (formatDateToLocal(expected) === completedPeriods[i]) current++;
            else {
                best = Math.max(best, current);
                current = 1;
            }
        }
        return Math.max(best, current);
    }

    const sortedDates = getHabitCompletedDates(habit);

    if (sortedDates.length === 0) return 0;

    let bestStreak = 1;
    let currentStreak = 1;

    for (let i = 1; i < sortedDates.length; i++) {
        const previousDate = parseLocalDate(sortedDates[i - 1]);
        const currentDate = parseLocalDate(sortedDates[i]);
        const nextExpectedDate = getNextTrackedDate(habit, previousDate);
        const diffDays = getDateDiffInDays(nextExpectedDate, currentDate);

        if (diffDays === 0) {
            currentStreak++;
        } else {
            bestStreak = Math.max(bestStreak, currentStreak);
            currentStreak = 1;
        }
    }

    return Math.max(bestStreak, currentStreak);
};

const getPreviousPeriodStart = (habit: Habit, date: Date) => {
    const previous = getPeriodStart(habit, date);
    if (habit.frequency === 'weekly') previous.setDate(previous.getDate() - 7);
    else previous.setMonth(previous.getMonth() - 1, 1);
    return previous;
};

const getNextPeriodStart = (habit: Habit, date: Date) => {
    const next = getPeriodStart(habit, date);
    if (habit.frequency === 'weekly') next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + 1, 1);
    return next;
};
